import { useState } from "react";
import {
  Box, Button, Grid, IconButton, MenuItem, Select,
  TextField, Typography, Chip,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import SaveIcon from "@mui/icons-material/Save";
import { updateToastError } from "../utils/notification-loading";

const FILTER_FIELDS = [
  { field: "signature",             headerName: "DETAILS",       type: "text",   operators: ["contains", "notContains", "equals", "notEquals"] },
  { field: "category",              headerName: "CATEGORY",        type: "text",   operators: ["contains", "notContains", "equals", "notEquals"] },
  { field: "severity",              headerName: "SEVERITY",        type: "select", operators: ["equals"], options: ["CRITICAL", "HIGH", "MEDIUM", "LOW"] },
  { field: "src_ip",                headerName: "SRC IP",          type: "text",   operators: ["contains", "notContains", "equals", "notEquals"] },
  { field: "src_port",              headerName: "SRC PORT",        type: "number", operators: ["equals", "gt", "lt"] },
  { field: "dest_ip",               headerName: "DEST IP",         type: "text",   operators: ["contains", "notContains", "equals", "notEquals"] },
  { field: "dest_port",             headerName: "DEST PORT",       type: "number", operators: ["equals", "gt", "lt"] },
  { field: "app_proto",             headerName: "PROTOCOL",        type: "text",   operators: ["contains", "notContains", "equals", "notEquals"] },
  { field: "count",                 headerName: "COUNT",           type: "number", operators: ["equals", "gt", "lt"] },
  { field: "suppressed",            headerName: "SUPPRESSED",      type: "boolean",operators: ["equals"] },
];

const SEVERITY_COLORS = {
  CRITICAL: "#db4f4a",
  HIGH: "#e2724e",
  MEDIUM: "#f0c050",
  LOW: "#4cceac",
};

const OPERATOR_LABELS = {
  contains: "contains",
  notContains: "not contains",
  equals: "equals",
  notEquals: "not equals",
  gt: ">",
  lt: "<",
};

function defaultValue(fieldDef) {
  if (fieldDef.type === "boolean") return true;
  if (fieldDef.type === "select") return fieldDef.options[0];
  if (fieldDef.type === "number") return "";
  return "";
}

function getFiltersWithoutLogic(list) {
  if (list.length > 0 && list[0].logic) {
    return list.map(({ logic, ...rest }) => rest);
  }
  return list;
}

const ValueInput = ({ filter, index, onChange }) => {
  const fieldDef = FILTER_FIELDS.find((f) => f.field === filter.field);
  if (!fieldDef) return null;

  if (fieldDef.type === "boolean") {
    return (
      <Select variant="standard" fullWidth
        value={filter.value === true || filter.value === "true" ? true : false}
        onChange={(e) => onChange(index, "value", e.target.value === "true" || e.target.value === true)}
      >
        <MenuItem value={true}>TRUE</MenuItem>
        <MenuItem value={false}>FALSE</MenuItem>
      </Select>
    );
  }

  if (fieldDef.type === "number") {
    return (
      <TextField
        variant="standard" fullWidth type="number"
        placeholder={fieldDef.headerName}
        value={filter.value ?? ""}
        onChange={(e) => onChange(index, "value", e.target.value === "" ? "" : Number(e.target.value))}
      />
    );
  }

  if (fieldDef.type === "select") {
    return (
      <Select variant="standard" fullWidth
        value={filter.value ?? fieldDef.options[0]}
        onChange={(e) => onChange(index, "value", e.target.value)}
      >
        {fieldDef.options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            <Chip
              label={opt} size="small"
              sx={{
                color: SEVERITY_COLORS[opt] || "#aaa",
                borderColor: SEVERITY_COLORS[opt] || "#aaa",
                backgroundColor: `${SEVERITY_COLORS[opt]}22` || "transparent",
                fontWeight: 700, fontSize: "0.7rem",
              }}
              variant="outlined"
            />
          </MenuItem>
        ))}
      </Select>
    );
  }

  return (
    <TextField
      variant="standard" fullWidth
      placeholder={fieldDef.headerName}
      value={filter.value || ""}
      onChange={(e) => onChange(index, "value", e.target.value)}
    />
  );
};

const OtSecurityCustomFilters = ({ filterModel, setFilterModel, onClose }) => {
  const [filtersList, setFiltersList] = useState(getFiltersWithoutLogic(filterModel));

  const handleChange = (index, key, value) => {
    const updated = [...filtersList];
    if (key === "field") {
      const fieldDef = FILTER_FIELDS.find((f) => f.field === value);
      updated[index] = {
        ...updated[index],
        field: value,
        operator: fieldDef.operators[0],
        value: defaultValue(fieldDef),
      };
    } else if (key === "operator") {
      updated[index] = { ...updated[index], operator: value };
    } else {
      updated[index] = { ...updated[index], [key]: value };
    }
    setFiltersList(updated);
  };

  const handleAddFilter = () => {
    const fieldDef = FILTER_FIELDS[0];
    const newFilter = {
      field: fieldDef.field,
      operator: fieldDef.operators[0],
      value: defaultValue(fieldDef),
    };
    if (filtersList.length > 0) newFilter.logic = "and";
    setFiltersList((prev) => [...prev, newFilter]);
  };

  const handleDelete = (index) => {
    let newList = filtersList.filter((_, i) => i !== index);
    if (index === 0 && newList.length > 0) {
      const { logic, ...rest } = newList[0];
      newList[0] = rest;
    }
    setFiltersList(newList);
  };

  const handleRemoveAll = () => {
    setFiltersList([]);
    setFilterModel([]);
  };

  const handleSave = () => {
    for (const f of filtersList) {
      if (f.value === null || f.value === undefined || f.value === "") {
        updateToastError("Invalid filter value");
        return;
      }
    }
    setFilterModel(filtersList);
    onClose?.();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "50vw" }}>
      {filtersList.length > 0 ? (
        filtersList.map((filter, index) => {
          const fieldDef = FILTER_FIELDS.find((f) => f.field === filter.field);
          return (
            <Box key={index} sx={{ width: "100%" }}>
              <Grid container spacing={2} alignItems="center" sx={{ padding: 2 }}>
                <Grid size={0.5}>
                  <IconButton color="error" onClick={() => handleDelete(index)} sx={{ minWidth: 0, padding: "6px" }}>
                    <ClearIcon />
                  </IconButton>
                </Grid>

                {filter.logic ? (
                  <Grid size={1.2}>
                    <Select variant="standard" fullWidth value={filter.logic}
                      onChange={(e) => handleChange(index, "logic", e.target.value)}>
                      <MenuItem value="and">AND</MenuItem>
                      <MenuItem value="or">OR</MenuItem>
                    </Select>
                  </Grid>
                ) : filtersList.length > 1 ? (
                  <Grid size={1.2} />
                ) : null}

                <Grid size={filtersList.length > 1 ? 2.8 : 3.5}>
                  <Select variant="standard" fullWidth value={filter.field || ""}
                    onChange={(e) => handleChange(index, "field", e.target.value)}>
                    {FILTER_FIELDS.map((f) => (
                      <MenuItem key={f.field} value={f.field}>{f.headerName}</MenuItem>
                    ))}
                  </Select>
                </Grid>

                <Grid size={2}>
                  <Select variant="standard" fullWidth value={filter.operator || ""}
                    onChange={(e) => handleChange(index, "operator", e.target.value)}>
                    {(fieldDef?.operators || []).map((op) => (
                      <MenuItem key={op} value={op}>{OPERATOR_LABELS[op] ?? op}</MenuItem>
                    ))}
                  </Select>
                </Grid>

                <Grid size={5}>
                  <ValueInput filter={filter} index={index} onChange={handleChange} />
                </Grid>
              </Grid>
            </Box>
          );
        })
      ) : (
        <Typography sx={{ fontSize: 16, fontFamily: "poppins", py: 2 }}>No filters</Typography>
      )}

      <Grid container spacing={2} display="flex" justifyContent="space-between" alignItems="center" sx={{ padding: 1, width: "100%" }}>
        <Grid size={4}>
          <Button fullWidth sx={{ fontFamily: "poppins", fontSize: 12 }} onClick={handleAddFilter}>
            <PlaylistAddIcon sx={{ marginRight: 1 }} /> ADD FILTER
          </Button>
        </Grid>
        <Grid size={4} display="flex" gap={2} justifyContent="flex-end">
          <Button
            color="error" variant="contained" disabled={filtersList.length === 0}
            fullWidth onClick={handleRemoveAll}
            sx={{ fontFamily: "poppins", fontSize: 12, "&:hover": { backgroundColor: "#f44336 !important", opacity: 0.8 } }}
          >
            <DeleteForeverIcon fontSize="small" sx={{ marginRight: 1 }} /> REMOVE ALL
          </Button>
          <Button fullWidth sx={{ fontFamily: "poppins", fontSize: 12 }} onClick={handleSave}>
            <SaveIcon fontSize="small" sx={{ marginRight: 1 }} /> APPLY FILTER
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OtSecurityCustomFilters;
